import React, { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';

import { useSharedTranslation } from '../hooks/useSharedTranslation';
import { Button, ButtonType } from '../Button/Button';
import { ViewMode } from './types';

interface Props {
  title: string;
  view: ViewMode;
  onView: (next: ViewMode) => void;
  onPrev: () => void;
  onToday: () => void;
  onNext: () => void;
  onSettings?: () => void;
  namespace?: string;
  trailing?: ReactNode;
}

export const CalendarToolbar = ({
  title,
  view,
  onView,
  onPrev,
  onToday,
  onNext,
  onSettings,
  namespace,
  trailing,
}: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespace);
  const stepLabel = view === 'day'
    ? t('pages.calendar.unitDay')
    : view === 'week'
      ? t('pages.calendar.unitWeek')
      : t('pages.calendar.unitMonth');

  return (
    <div className='bg-neutral-100 border border-neutral-200 rounded-t flex flex-wrap items-center gap-2 px-3 py-2'>
      <div className='flex items-center gap-1'>
        <Button
          id='arrow-left'
          buttonType={ButtonType.secondary}
          aria-label={t('pages.calendar.previousLabel', { unit: stepLabel })}
          className='!w-9 !h-9 !p-0 !min-w-0 flex items-center justify-center'
          onClick={onPrev}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <Button
          id='today'
          buttonType={ButtonType.secondary}
          onClick={onToday}
        >
          {t('pages.calendar.today')}
        </Button>
        <Button
          id='arrow-right'
          buttonType={ButtonType.secondary}
          aria-label={t('pages.calendar.nextLabel', { unit: stepLabel })}
          className='!w-9 !h-9 !p-0 !min-w-0 flex items-center justify-center'
          onClick={onNext}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </div>
      <h3 className='text-lg font-semibold mx-2 my-0 flex-1 min-w-0 truncate text-neutral-800 leading-9'>
        {title}
      </h3>
      <div className='inline-flex rounded-full border border-sapphire overflow-hidden bg-white'>
        {(['day', 'week', 'month'] as const).map((v) => {
          const isActive = view === v;
          const label = v === 'day'
            ? t('pages.calendar.viewDay')
            : v === 'week'
              ? t('pages.calendar.viewWeek')
              : t('pages.calendar.viewMonth');
          return (
            <Button
              key={v}
              id={v}
              buttonType={ButtonType.tertiary}
              onClick={(): void => onView(v)}
              aria-pressed={isActive}
              className={`!rounded-none px-4 !py-1.5 text-sm font-semibold transition-colors border-r border-sapphire last:border-r-0 ${
                isActive
                  ? 'bg-sapphire !text-white hover:bg-sapphire-700'
                  : 'bg-white !text-sapphire hover:bg-sapphire-50'
              }`}
            >
              {label}
            </Button>
          );
        })}
      </div>
      {trailing}
      {onSettings && (
        <Button
          id='edit-settings-sm'
          buttonType={ButtonType.secondary}
          className='!w-9 !h-9 !p-0 !min-w-0 flex items-center justify-center'
          onClick={onSettings}
          aria-label={t('pages.calendar.settingsAria')}
        >
          <FontAwesomeIcon icon={faGear} />
        </Button>
      )}
    </div>
  );
};
